import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Switch, IconButton, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Divider } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { Form, Formik, FieldArray } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { get, postNoResponse } from "utils/WebService";
import { successAlert, warningAlert } from "utils/Functions";

export default function CampanhaCreateEdit({ open, onClose, id }) {
  const defaultParcelamento = { cvParcelaMinima: 1, cvParcelaMaxima: 1, cvPercentualMinimoEntrada: 0, cvDescontoMaxPrincipal: 0, cvDescontoMaxJuros: 0, cvDescontoMaxMulta: 0, cvDescontoMaxHonorarios: 0 };
  const defaultRegra = { cvAtrasoMinimoDias: 0, cvAtrasoMaximoDias: "", cvAnoVencimento: "", cvTipoCorrecao: 1, parcelamentos: [{ ...defaultParcelamento }] };

  const [initialValues, setInitialValues] = useState({
    id: null,
    clienteId: "",
    subCarteiraId: "",
    ccNome: "",
    cvTipoAplicacao: "",
    cbPadrao: false,
    cbCorrigeAVencer: false,
    cvMultaPercentual: 0,
    cvJurosMensalPercentual: 0,
    cvTipoJuros: 1,
    cvHonorariosPercentual: 0,
    cvTipoCorrecao: 1,
    cvTipoIndice: "",
    cdInicio: "",
    cdFim: "",
    cbAtivo: true,
    cvPrazoMaximoEntrada: "",
    cnValorMinimoParcela: "",
    regras: [{ ...defaultRegra }]
  });
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [subCarteiras, setSubCarteiras] = useState([]);
  const [indices, setIndices] = useState([]);

  const fetchSubCarteiras = (clienteId) => {
    if (!clienteId) {
      setSubCarteiras([]);
      return;
    }
    get(`/pessoa/by-id/${clienteId}`).then(res => setSubCarteiras(res.subCarteiras || []));
  };

  useEffect(() => {
    get('/pessoa/list-all').then(res => setClientes(res.list || []));
    get('/indice/list-all').then(res => setIndices(res.list || []));
  }, []);

  useEffect(() => {
    if (id) {
      get(`/campanha/by-id/${id}`)
        .then((data) => {
          const firstRegra = data.regras?.length ? data.regras[0] : null;
          setInitialValues({
            id: data.id,
            clienteId: data.cliente?.id || "",
            subCarteiraId: data.subCarteira?.id || "",
            ccNome: data.ccNome || "",
            cvTipoAplicacao: data.cvTipoAplicacao || "",
            cbPadrao: data.cbPadrao || false,
            cbCorrigeAVencer: data.cbCorrigeAVencer || false,
            cvMultaPercentual: firstRegra?.cvMultaPercentual || 0,
            cvJurosMensalPercentual: firstRegra?.cvJurosMensalPercentual || 0,
            cvTipoJuros: firstRegra?.cvTipoJuros || 1,
            cvHonorariosPercentual: firstRegra?.cvHonorariosPercentual || 0,
            cvTipoCorrecao: firstRegra?.cvTipoCorrecao || 1,
            cvTipoIndice: firstRegra?.cvTipoIndice || "",
            cdInicio: data.cdInicio ? (typeof data.cdInicio === 'number' ? new Date(data.cdInicio).toISOString().split('T')[0] : data.cdInicio.split('T')[0]) : "",
            cdFim: data.cdFim ? (typeof data.cdFim === 'number' ? new Date(data.cdFim).toISOString().split('T')[0] : data.cdFim.split('T')[0]) : "",
            cbAtivo: data.cbAtivo !== false,
            cvPrazoMaximoEntrada: data.cvPrazoMaximoEntrada !== null ? data.cvPrazoMaximoEntrada : "",
            cnValorMinimoParcela: data.cnValorMinimoParcela !== null ? data.cnValorMinimoParcela : "",
            regras: data.regras?.length ? data.regras.map(r => ({
              ...r,
              parcelamentos: r.parcelamentos?.length ? r.parcelamentos : [{ ...defaultParcelamento }]
            })) : [{ ...defaultRegra }]
          });
          if (data.cliente?.id) {
            fetchSubCarteiras(data.cliente.id);
          }
        })
        .catch(() => warningAlert("Erro ao carregar dados da campanha"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      ...values,
      cliente: values.clienteId ? { id: values.clienteId } : null,
      subCarteira: values.subCarteiraId ? { id: values.subCarteiraId } : null,
      cdInicio: values.cdInicio ? new Date(values.cdInicio).toISOString() : null,
      cdFim: values.cdFim ? new Date(values.cdFim).toISOString() : null,
      cvPrazoMaximoEntrada: values.cvPrazoMaximoEntrada === "" ? null : parseInt(values.cvPrazoMaximoEntrada, 10),
      cnValorMinimoParcela: values.cnValorMinimoParcela === "" ? null : parseFloat(values.cnValorMinimoParcela),
      regras: values.regras.map(r => ({
        ...r,
        cvTipoCorrecao: values.cvTipoCorrecao,
        cvTipoIndice: values.cvTipoIndice ? parseInt(values.cvTipoIndice, 10) : null,
        cvMultaPercentual: values.cvMultaPercentual,
        cvJurosMensalPercentual: values.cvJurosMensalPercentual,
        cvTipoJuros: values.cvTipoJuros,
        cvHonorariosPercentual: values.cvHonorariosPercentual,
        cvAtrasoMaximoDias: r.cvAtrasoMaximoDias === "" ? null : parseInt(r.cvAtrasoMaximoDias, 10),
        cvAtrasoMinimoDias: r.cvAtrasoMinimoDias === "" ? 0 : parseInt(r.cvAtrasoMinimoDias, 10),
        cvAnoVencimento: r.cvAnoVencimento === "" || r.cvAnoVencimento == null ? null : parseInt(r.cvAnoVencimento, 10),
        parcelamentos: r.parcelamentos.map(p => ({
          ...p,
          cvParcelaMinima: p.cvParcelaMinima === "" ? 1 : parseInt(p.cvParcelaMinima, 10),
          cvParcelaMaxima: p.cvParcelaMaxima === "" ? 1 : parseInt(p.cvParcelaMaxima, 10),
          cvPercentualMinimoEntrada: p.cvPercentualMinimoEntrada === "" ? 0 : parseFloat(p.cvPercentualMinimoEntrada),
          cvDescontoMaxPrincipal: p.cvDescontoMaxPrincipal === "" ? 0 : parseFloat(p.cvDescontoMaxPrincipal),
          cvDescontoMaxJuros: p.cvDescontoMaxJuros === "" ? 0 : parseFloat(p.cvDescontoMaxJuros),
          cvDescontoMaxMulta: p.cvDescontoMaxMulta === "" ? 0 : parseFloat(p.cvDescontoMaxMulta),
          cvDescontoMaxHonorarios: p.cvDescontoMaxHonorarios === "" ? 0 : parseFloat(p.cvDescontoMaxHonorarios)
        }))
      }))
    };

    postNoResponse("/campanha/save", payload)
      .then(() => {
        successAlert("Campanha salva com sucesso!");
        onClose();
      })
      .catch((err) => {
        console.error(err);
        warningAlert("Erro ao salvar campanha.");
      })
      .finally(() => setSubmitting(false));
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
          <CircularProgress color="info" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <MDTypography variant="h6">{id ? "Editar Campanha" : "Nova Campanha"}</MDTypography>
        <IconButton onClick={onClose} size="small">
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormField
                    select
                    SelectProps={{ native: true }}
                    label="Cliente"
                    name="clienteId"
                    value={values.clienteId}
                    onChange={(e) => {
                      handleChange(e);
                      fetchSubCarteiras(e.target.value);
                      setFieldValue("subCarteiraId", "");
                    }}
                  >
                    <option value="">(Todos)</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nomeCompleto || c.ccNome}</option>)}
                  </FormField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField
                    select
                    SelectProps={{ native: true }}
                    label="Sub Carteira"
                    name="subCarteiraId"
                    value={values.subCarteiraId}
                    onChange={handleChange}
                    disabled={!values.clienteId || subCarteiras.length === 0}
                  >
                    <option value="">(Todas)</option>
                    {subCarteiras.map(s => <option key={s.id} value={s.id}>{s.ccNome}</option>)}
                  </FormField>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormField
                    label="Nome da Campanha *"
                    name="ccNome"
                    value={values.ccNome}
                    onChange={handleChange}
                    placeholder="Ex: Feirão Natal"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormField
                    select
                    SelectProps={{ native: true }}
                    label="Tipo de Aplicação *"
                    name="cvTipoAplicacao"
                    value={values.cvTipoAplicacao}
                    onChange={handleChange}
                  >
                    <option value="" disabled hidden>Selecionar</option>
                    <option value={1}>Global (Devedor)</option>
                    <option value={2}>Por Faixa de Atraso</option>
                    <option value={3}>Marcação Especial</option>
                    <option value={4}>Por Ano de Vencimento</option>
                  </FormField>
                </Grid>
                <Grid item xs={12} md={3} display="flex" alignItems="center" mt={2}>
                  <MDTypography variant="button" sx={{ mr: 1 }} color="text">Padrão</MDTypography>
                  <Switch checked={values.cbPadrao} onChange={(e) => setFieldValue('cbPadrao', e.target.checked)} />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormField
                    type="date"
                    label="Data Início"
                    name="cdInicio"
                    value={values.cdInicio}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormField
                    type="date"
                    label="Data Fim"
                    name="cdFim"
                    value={values.cdFim}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2} display="flex" alignItems="center" mt={2}>
                  <MDTypography variant="button" sx={{ mr: 1 }} color="text">Ativo</MDTypography>
                  <Switch checked={values.cbAtivo} onChange={(e) => setFieldValue('cbAtivo', e.target.checked)} />
                </Grid>
                <Grid item xs={12} md={2} display="flex" alignItems="center" mt={2}>
                  <MDTypography variant="button" sx={{ mr: 1 }} color="text">Corrige a Vencer</MDTypography>
                  <Switch checked={values.cbCorrigeAVencer} onChange={(e) => setFieldValue('cbCorrigeAVencer', e.target.checked)} />
                </Grid>
              </Grid>

              <MDBox mt={3}>
                <Divider />
                <MDTypography variant="h6" mb={2}>Limites de Negociação</MDTypography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <FormField type="number" label="Prazo Máx. Entrada (Dias)" name="cvPrazoMaximoEntrada" value={values.cvPrazoMaximoEntrada} onChange={handleChange} placeholder="Ex: 5" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormField type="number" label="Valor Mín. Parcela (R$)" name="cnValorMinimoParcela" value={values.cnValorMinimoParcela} onChange={handleChange} placeholder="Ex: 30.00" />
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox mt={3}>
                <Divider />
                <MDTypography variant="h6" mb={2}>Padrões de Correção da Campanha</MDTypography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={2}>
                    <FormField type="number" label="Multa (%)" name="cvMultaPercentual" value={values.cvMultaPercentual} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormField type="number" label="Juros Mensal (%)" name="cvJurosMensalPercentual" value={values.cvJurosMensalPercentual} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormField
                      select
                      SelectProps={{ native: true }}
                      label="Tipo de Juros"
                      name="cvTipoJuros"
                      value={values.cvTipoJuros}
                      onChange={handleChange}
                    >
                      <option value={1}>Simples</option>
                      <option value={2}>Composto</option>
                    </FormField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormField type="number" label="Honorários (%)" name="cvHonorariosPercentual" value={values.cvHonorariosPercentual} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormField
                      select
                      SelectProps={{ native: true }}
                      label="Tipo de Correção"
                      name="cvTipoCorrecao"
                      value={values.cvTipoCorrecao}
                      onChange={handleChange}
                    >
                      <option value={1}>Pro Rata Die</option>
                      <option value={2}>Mensal Cheio</option>
                      <option value={3}>Sem Correção</option>
                    </FormField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormField
                      select
                      SelectProps={{ native: true }}
                      label="Índice de Correção"
                      name="cvTipoIndice"
                      value={values.cvTipoIndice}
                      onChange={handleChange}
                    >
                      <option value="">(Nenhum)</option>
                      <option value={1}>SELIC</option>
                      <option value={2}>IPCA</option>
                      <option value={3}>INPC</option>
                      <option value={4}>IGPM</option>
                    </FormField>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox mt={4} mb={1} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6">Faixas de Atraso e Regras de Parcelamento</MDTypography>
              </MDBox>

              <FieldArray name="regras">
                {({ push, remove }) => (
                  <MDBox>
                    {values.regras.map((regra, index) => (
                      <MDBox key={index} mb={3} p={2} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
                        {String(values.cvTipoAplicacao) === "2" && (
                          <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} md={3}>
                              <FormField type="number" label="Atraso Mínimo" name={`regras.${index}.cvAtrasoMinimoDias`} value={regra.cvAtrasoMinimoDias} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <FormField type="number" label="Atraso Máximo" placeholder="Infinito" name={`regras.${index}.cvAtrasoMaximoDias`} value={regra.cvAtrasoMaximoDias} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} md={6} display="flex" alignItems="center" justifyContent="flex-end">
                              <MDButton color="error" variant="text" onClick={() => remove(index)}>
                                <Icon>delete</Icon> Remover Faixa
                              </MDButton>
                            </Grid>
                          </Grid>
                        )}

                        {String(values.cvTipoAplicacao) === "4" && (
                          <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} md={4}>
                              <FormField type="number" label="Ano de Vencimento" placeholder="Ex: 2024" name={`regras.${index}.cvAnoVencimento`} value={regra.cvAnoVencimento} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={12} md={8} display="flex" alignItems="center" justifyContent="flex-end">
                              <MDButton color="error" variant="text" onClick={() => remove(index)}>
                                <Icon>delete</Icon> Remover Regra
                              </MDButton>
                            </Grid>
                          </Grid>
                        )}
                        
                        <MDTypography variant="caption" fontWeight="bold" color="text">Tabela de Descontos por Parcelamento</MDTypography>
                        <FieldArray name={`regras.${index}.parcelamentos`}>
                          {(parcProps) => (
                            <MDBox mt={1}>
                              <Table size="small">
                                <TableHead sx={{ display: "table-header-group" }}>
                                  <TableRow>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Parc. Mín</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Parc. Máx</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Entrada Mín (%)</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Desc Principal (%)</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Desc Juros (%)</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Desc Multa (%)</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="medium" color="text">Desc Honorários (%)</MDTypography></TableCell>
                                    <TableCell align="center"><MDTypography variant="caption" fontWeight="medium" color="text">Ações</MDTypography></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {regra.parcelamentos.map((parc, pIndex) => (
                                    <TableRow key={pIndex}>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvParcelaMinima`} value={parc.cvParcelaMinima} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvParcelaMaxima`} value={parc.cvParcelaMaxima} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvPercentualMinimoEntrada`} value={parc.cvPercentualMinimoEntrada} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvDescontoMaxPrincipal`} value={parc.cvDescontoMaxPrincipal} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvDescontoMaxJuros`} value={parc.cvDescontoMaxJuros} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvDescontoMaxMulta`} value={parc.cvDescontoMaxMulta} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1 }}>
                                        <MDInput type="number" size="small" fullWidth name={`regras.${index}.parcelamentos.${pIndex}.cvDescontoMaxHonorarios`} value={parc.cvDescontoMaxHonorarios} onChange={handleChange} />
                                      </TableCell>
                                      <TableCell align="center" sx={{ py: 1 }}>
                                        <IconButton color="error" onClick={() => parcProps.remove(pIndex)}>
                                          <Icon fontSize="small">delete</Icon>
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              <MDBox mt={1} mb={1}>
                                <MDButton variant="outlined" color="dark" size="small" onClick={() => parcProps.push({ ...defaultParcelamento })}>
                                  <Icon>add</Icon>&nbsp;Novo Parcelamento
                                </MDButton>
                              </MDBox>
                            </MDBox>
                          )}
                        </FieldArray>
                      </MDBox>
                    ))}

                    {(String(values.cvTipoAplicacao) === "2" || String(values.cvTipoAplicacao) === "4") && (
                      <MDBox mt={2}>
                        <MDButton variant="outlined" color="info" size="small" onClick={() => push({ ...defaultRegra })}>
                          <Icon>add</Icon>&nbsp;Nova Faixa / Regra
                        </MDButton>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </FieldArray>

            </DialogContent>
            <DialogActions>
              <MDButton color="secondary" onClick={onClose}>Cancelar</MDButton>
              <MDButton color="info" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Salvando..." : "Salvar"}
              </MDButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
